export interface TestProps {
  testId?: string;
}

export interface JWT {
  [x: string]: string | number | boolean | string[] | number[];
}

export type PermissionID = "none" | "read" | "edit";
export type PermissionType = "user" | "group";
export type EntityWithPermissions = "group" | "directive";

export type ChartActionID =
  | "x"
  | "y"
  | "legend"
  | "MIS"
  | "signalEvents"
  | "granularity"
  | "exposure"
  | "exposureBucket"
  | "groupBy"
  | "bucketBy"
  | "measure"
  | "logarithmic";

export type ChartActionType =
  | "dropdownSelect"
  | "dropdownSelectNested"
  | "label"
  | "attrNameValue"
  | "boolean"
  | "";

export type MileageUnit = "mi" | "km";

export type Sort = "asc" | "desc";
export type SortBy = Record<string, Sort>;

export type PercentString = `${number}%`;

export type PopulationLine = {
  key: string;
  color: string;
  label: string;
  dashed: boolean;
};

export enum ResourceTypeEnum {
  VEHICLE = "vehicle",
}

export enum EventTypeEnum {
  SIGNAL_EVENT = "signalEvent",
  CLAIM = "claim",
  SENSOR_READING = "sensorReading",
  INSPECTION = "inspection",
  REPAIR = "repair",
  CUSTOM_RECORD = "customRecord",
  ISSUE = "issue",
  WORK_ORDER = "workOrder",
  FLEET = "fleet",
  VEHICLE = "vehicle",
  REPAIR_LABOR = "repairLabor",
  REPAIR_PARTS = "repairParts",
  ASSET = "asset",
}

export const EventTypeEnumCapitalized = Object.fromEntries(
  Object.entries(EventTypeEnum).map(([key, value]) => [
    key,
    value.charAt(0).toUpperCase() + value.slice(1),
  ])
) as {
  [K in keyof typeof EventTypeEnum]: Capitalize<(typeof EventTypeEnum)[K]>;
};

export type EventTypeEnumCapitalizedType =
  (typeof EventTypeEnumCapitalized)[keyof typeof EventTypeEnumCapitalized];

export type AttachmentType =
  EventTypeEnum.CLAIM | EventTypeEnum.ISSUE | ResourceTypeEnum.VEHICLE;

export type CurrencyCode = "USD" | "EUR" | "JPY" | null;

export enum DataType {
  DATE = "date",
  DATE_WITH_TIME = "dateWithTime",
  DATE_YEAR_MONTH = "dateYearMonth",
  DATE_YEAR_MONTH_DAY = "dateYearMonthDay",
  DATE_WITH_TIME_OR_TIME_AGO_IF_RECENT = "dateWithTimeOrTimeAgoIfRecent",
  DATE_UTC = "dateUTC",
  DATE_WITH_TIME_UTC = "dateWithTimeUTC",
  DATE_WITH_TIME_NO_TZ = "dateWithTimeNoTZ",
  NUMBER = "number",
  STRING = "string",
  VERBATIM = "verbatim", // shown exactly as given: no separators, no underscore prettifying
  BOOLEAN = "boolean",
  PERCENTAGE = "percentage", // adds %
  RATIO_PERCENTAGE = "ratioPercentage", // multiplies by 100 and adds %
  OBJECT = "object",
  JSX = "jsx",
}

declare module "@mui/material/Button" {
  interface ButtonPropsSizeOverrides {
    xs: true;
  }
  interface ButtonPropsColorOverrides {
    tertiary: true;
  }
}

declare module "@mui/material/IconButton" {
  interface IconButtonPropsSizeOverrides {
    xs: true;
  }
  interface IconButtonPropsColorOverrides {
    tertiary: true;
    transparent: true;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    body3: true;
    overline: true;
  }
}

declare module "@mui/material/TextField" {
  interface TextFieldPropsSizeOverrides {
    large: true;
  }
}

declare module "@mui/material/InputBase" {
  interface InputBasePropsSizeOverrides {
    large: true;
  }
}

declare module "@mui/material/Autocomplete" {
  interface AutocompletePropsSizeOverrides {
    large: true;
  }
}

declare module "@mui/material/Chip" {
  interface ChipPropsSizeOverrides {
    large: true;
  }
  interface ChipPropsColorOverrides {
    ai: true;
    neutral: true;
  }
}
